/**
 * OnboardingChecklist — the "Get Your First RV Match Lead" panel that shows
 * up top of a new dealer's Overview tab until they've completed setup AND
 * received their first real (non-test) lead.
 *
 * Progress is a UNION of:
 *   1) Persisted activation events from the backend (GET /api/dealer/activation)
 *      so completion survives logout + device switch.
 *   2) Live-derived signals from data the dashboard already loads (leads,
 *      analytics, dealer profile) so a dealer who took an action BEFORE we
 *      shipped this feature still gets the correct green check.
 *
 * Once a dealer has all 6 steps complete AND at least one real lead in their
 * pipeline the panel collapses into a compact "Setup Complete" card that can
 * be re-expanded from the "?" help modal (event = ``onboarding_dismissed``).
 */
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, Circle, Sparkles, X, Package, Palette, ClipboardList,
  Copy, MailCheck, FlaskConical, ExternalLink, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () =>
  localStorage.getItem('dealer_token') ||
  localStorage.getItem('token') ||
  sessionStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

const OnboardingChecklist = ({
  dealer,
  leads = [],
  analytics = null,
  isDark = true,
  onOpenHelp,
  onGoTo,
  onTestLeadCreated,
}) => {
  // Backend-persisted activation events (map of event → ISO timestamp)
  const [events, setEvents] = useState({});
  const [collapsed, setCollapsed] = useState(false);

  // Load persisted state on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/dealer/activation`, { headers: authHeaders() });
        if (!cancelled) setEvents(res.data?.events || {});
      } catch { /* silent — checklist still works from live-derived signals */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const recordEvent = useCallback(async (event) => {
    // Optimistic update so the UI ticks instantly.
    setEvents((prev) => ({ ...prev, [event]: prev[event] || new Date().toISOString() }));
    try {
      await axios.post(`${API}/dealer/activation/event`, { event }, { headers: authHeaders() });
    } catch { /* silent — optimistic state still reflects intent */ }
  }, []);

  // ---------- action handlers wired to each step ----------
  const copyQuizLink = async () => {
    const slug = (dealer?.slug || '').trim();
    const url = slug
      ? `${window.location.origin}/${slug}`
      : `${window.location.origin}/quiz`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied ${url}`);
    } catch {
      toast.error('Copy failed — long-press the link in Marketing → Sharing to copy manually.');
    }
    recordEvent('quiz_link_copied');
  };

  const openPublicPage = () => {
    const slug = (dealer?.slug || '').trim();
    if (!slug) {
      toast.error('Add a slug in Marketing → Sharing first.');
      onGoTo?.('marketing');
      return;
    }
    window.open(`${window.location.origin}/${slug}`, '_blank', 'noopener');
    recordEvent('public_page_viewed');
  };

  const createTestLead = async () => {
    try {
      const res = await axios.post(`${API}/dealer/test-lead`, {}, { headers: authHeaders() });
      if (res.data?.success) {
        toast.success('Test lead created — check your Pipeline.');
        recordEvent('test_lead_created');
        onTestLeadCreated?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create test lead.');
    }
  };

  const gotoInventory = () => { onGoTo?.('inventory'); recordEvent('inventory_added'); };
  const gotoIntakeForm = () => { onGoTo?.('intake-form'); recordEvent('intake_form_reviewed'); };
  const gotoMarketing = () => { onGoTo?.('marketing'); recordEvent('dealer_profile_completed'); };
  const gotoEmailCenter = () => { onGoTo?.('email'); };

  // ---------- progress derivation ----------
  const items = useMemo(() => {
    const hasInventory = !!(analytics?.inventory_count || dealer?.inventory_count);
    const hasBusinessCard = !!(dealer?.business_card_url);
    const hasCustomSlug = !!(dealer?.slug_history && dealer.slug_history.length > 0);
    const hasIntakeCustomization = !!(dealer?.quiz_customization && Object.keys(dealer.quiz_customization).length > 0);
    const hasEmailSignature = !!(dealer?.signature_text || dealer?.signature_image_url);
    const hasTestLead = leads.some((l) => l.is_test);
    const hasRealLead = leads.some((l) => !l.is_test);

    return [
      {
        id: 'inventory',
        icon: Package,
        label: 'Add or import advisor inventory',
        done: hasInventory || !!events.inventory_added,
        primary: { text: 'Import Inventory', onClick: gotoInventory },
      },
      {
        id: 'profile',
        icon: Palette,
        label: 'Review advisor profile and branding',
        done: hasBusinessCard || hasCustomSlug || !!events.dealer_profile_completed,
        primary: { text: 'Review Advisor Profile', onClick: gotoMarketing },
      },
      {
        id: 'link',
        icon: Copy,
        label: "Copy your advisor RV Match form link",
        done: !!events.quiz_link_copied,
        primary: { text: 'Copy My Form Link', onClick: copyQuizLink },
        secondary: { text: 'View Public Page', icon: ExternalLink, onClick: openPublicPage },
      },
      {
        id: 'email',
        icon: MailCheck,
        label: 'Connect or confirm email notifications',
        done: hasEmailSignature || !!events.first_followup_sent,
        primary: { text: 'Open Email Center', onClick: gotoEmailCenter },
      },
      {
        id: 'test',
        icon: FlaskConical,
        label: 'Create one test lead and verify the full experience',
        done: hasTestLead || !!events.test_lead_created,
        primary: { text: 'Create Test Lead', onClick: createTestLead },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealer, leads, analytics, events]);

  const completed = items.filter((i) => i.done).length;
  const total = items.length;
  const pct = Math.round((completed / total) * 100);
  const allDone = completed === total;
  const hasRealLead = leads.some((l) => !l.is_test);

  // Auto-collapse once the dealer has finished setup AND received a real lead.
  useEffect(() => {
    if (allDone && hasRealLead) setCollapsed(true);
  }, [allDone, hasRealLead]);

  // -------- collapsed "Setup Complete" chip --------
  if (collapsed) {
    return (
      <div
        className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${
          isDark ? 'bg-emerald-500/10 border-emerald-500/25' : 'bg-emerald-50 border-emerald-200'
        }`}
        data-testid="onboarding-collapsed"
      >
        <CheckCircle2 size={18} className={isDark ? 'text-emerald-300' : 'text-emerald-700'} />
        <span className={`text-sm font-semibold flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Setup Complete — nice work!
        </span>
        <button
          type="button"
          onClick={() => { setCollapsed(false); recordEvent('onboarding_reopened'); }}
          className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
            isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-900 hover:bg-black text-white'
          }`}
          data-testid="onboarding-reopen"
        >
          Show checklist
        </button>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border p-5 sm:p-6 relative overflow-hidden ${
        isDark
          ? 'bg-gradient-to-br from-blue-950/40 via-slate-900/60 to-amber-950/30 border-white/10'
          : 'bg-gradient-to-br from-blue-50 via-white to-amber-50 border-gray-200'
      }`}
      data-testid="onboarding-checklist"
    >
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />

      <div className="relative flex flex-wrap items-start gap-3 mb-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
          isDark ? 'bg-amber-400/15 text-amber-300' : 'bg-amber-100 text-amber-700'
        }`}>
          <Sparkles size={20} />
        </div>
        <div className="w-full order-3 sm:order-none sm:w-auto sm:flex-1 min-w-0">
          <h2 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Get Your First RV Match Lead
          </h2>
          <p className={`text-sm mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
            Complete your dealership setup and start sending shoppers through your personalized RV Match experience.
          </p>
          <p className={`text-xs mt-1 font-semibold ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
            Dealership Setup: {completed} of {total} steps complete · {pct}%
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {onOpenHelp && (
            <button
              type="button"
              onClick={onOpenHelp}
              className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
                isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-900 hover:bg-black text-white'
              }`}
              data-testid="onboarding-open-help"
            >
              Open guide
            </button>
          )}
          <button
            type="button"
            onClick={() => { setCollapsed(true); recordEvent('onboarding_dismissed'); }}
            aria-label="Collapse onboarding"
            className={`p-1.5 rounded-md ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-200 text-gray-500'}`}
            data-testid="onboarding-dismiss"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className={`relative h-2 w-full rounded-full overflow-hidden mb-5 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
        <div
          className="h-full bg-gradient-to-r from-blue-500 via-cyan-400 to-amber-400 transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
          data-testid="onboarding-progress"
          data-pct={pct}
        />
      </div>

      {/* Items — each row has its own action button(s) */}
      <ul className="relative grid grid-cols-1 md:grid-cols-2 gap-2">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <li key={it.id}>
              <div
                className={`h-full flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                  it.done
                    ? isDark
                      ? 'border-emerald-500/20 bg-emerald-500/5'
                      : 'border-emerald-200 bg-emerald-50'
                    : isDark
                    ? 'border-white/10 hover:border-white/25 hover:bg-white/5'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
                data-testid={`onboarding-item-${it.id}`}
              >
                {it.done ? (
                  <CheckCircle2 size={18} className={`shrink-0 mt-0.5 ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`} />
                ) : (
                  <Icon size={18} className={`shrink-0 mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
                )}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${
                    it.done
                      ? isDark ? 'text-slate-400 line-through' : 'text-gray-500 line-through'
                      : isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {it.label}
                  </div>
                  {!it.done && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {it.primary && (
                        <button
                          type="button"
                          onClick={it.primary.onClick}
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
                            isDark
                              ? 'bg-amber-400 text-black hover:bg-amber-300'
                              : 'bg-amber-500 text-white hover:bg-amber-600'
                          }`}
                          data-testid={`onboarding-action-${it.id}`}
                        >
                          {it.primary.text}
                          <ChevronRight size={12} />
                        </button>
                      )}
                      {it.secondary && (
                        <button
                          type="button"
                          onClick={it.secondary.onClick}
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${
                            isDark
                              ? 'border-white/15 text-white hover:bg-white/10'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                          }`}
                          data-testid={`onboarding-secondary-${it.id}`}
                        >
                          {it.secondary.icon ? <it.secondary.icon size={12} /> : null}
                          {it.secondary.text}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OnboardingChecklist;
